import { css } from '@emotion/react';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { useTranslation } from 'react-i18next';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { tokens, Loader, Link } from '@hotjar/hotsauce';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useSubscription } from '@hotjar/webapp/hooks/useSubscription';
import { SESSION_QUERY_KEY } from '@hotjar/webapp/features/Settings/hooks';
import { Icon } from '@hotjar/webapp/components/Icon';
import type { Site } from '@hotjar/site';
import { Switch } from '@hotjar/webapp/components/Switch';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { getHasData } from '@hotjar/webapp/lib/getHasData';
import { CAPTURE_MODE } from '@hotjar/webapp/features/Settings/types';
import { hasUnlimitedSessions } from '@hotjar/webapp/domains/revenue/plan';
import { useLimit } from '@hotjar/allowances';

import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { sessionsSettingsApi } from '../api';

const Container = styled.div`
  letter-spacing: 0.1px;
  line-height: 150%;
  padding: 22px 24px 26px 32px;
  border: 1px solid ${tokens.color.ui_border};
  box-sizing: border-box;
  border-radius: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Disabled = ({ isWarning }: { isWarning: boolean }) => {
  const { t } = useTranslation();

  return (
    <span
      css={css`
        text-transform: uppercase;
        color: ${isWarning ? tokens.color.neutral_700 : tokens.color.magenta_600};
      `}
    >
      {t('common:site-settings.session-capture.disabled')}
    </span>
  );
};

const Enabled = ({ isWarning }: { isWarning: boolean }) => {
  const { t } = useTranslation();
  return (
    <span
      css={css`
        text-transform: uppercase;
        color: ${isWarning ? tokens.color.neutral_700 : tokens.color.mint_600};
      `}
    >
      {t('common:site-settings.session-capture.enabled')}
    </span>
  );
};

const AlertIcon = styled(Icon)`
  margin-top: 4px;
  margin-right: 8px;
  padding: 3px;
  border-radius: 50%;
  box-sizing: border-box;
  background-color: ${tokens.color.ember_600};
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
`;

const Info = styled.div`
  display: flex;
  font-weight: normal;
  font-size: 14px;
  margin-top: 6px;
`;

const StyledLink = styled.a`
  color: inherit;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

interface SessionCaptureProps {
  site: Site;
}

const SessionCapture = ({ site }: SessionCaptureProps): JSX.Element => {
  const { t } = useTranslation();

  const { upsell } = useSubscription();
  const { show: showModalTrackingCode } = useModalTrackingCode();
  const [isEnabled, setIsEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const unlimitedSessions = hasUnlimitedSessions(site);

  const { error } = useNotify();

  const ccDailyAllowance = useLimit('analytics.daily_cc_sessions', {
    id: String(site.id),
    type: 'site',
  });
  const { click } = useTrack();

  const { hasPermission } = useAuthorization();
  const hasSessionSettingsUpdatePermission = hasPermission(
    'site',
    `${site.id}`,
    'session-settings-update',
  );

  const trackClick = (elementName: string) => {
    click(elementName, 'site.settings-session_capture_toggle', {
      account_id: site.account.id,
      organization_id: site.organization.id,
      site_id: site.id,
      state_event_was_triggered_on: 'site.settings',
      url_event_was_triggered_on: `/site/${site.id}/settings`,
    });
  };

  useEffect(() => {
    // get switch state from api
    (async () => {
      setIsLoading(true);
      const response = await sessionsSettingsApi.get(site.id);
      if (response.success) {
        setIsEnabled(response.data?.session_capture_mode === CAPTURE_MODE.ON);
        // minimum loading time, to not have flickering
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    })();
  }, [site.id]);

  const saveCaptureMode = useCallback(
    async (enabled: boolean) => {
      setIsEnabled(enabled);
      // update api
      const response = await sessionsSettingsApi.post(site.id, 'update_session_capture_mode', {
        session_capture_mode: enabled ? CAPTURE_MODE.ON : CAPTURE_MODE.OFF,
      });
      // on success on don't do anything
      // on error we show an error message and switch back
      if (!response.success) {
        error(t('common:site-settings.session-capture.save-error'));
        setIsEnabled(!enabled);
      }
      queryClient.invalidateQueries(SESSION_QUERY_KEY);
    },
    [error, site.id, t],
  );

  if (isLoading) {
    return (
      <Container>
        <Loader
          isLoading
          css={css`
            margin: 6px auto;
          `}
        />
      </Container>
    );
  }

  const hasData = getHasData(site.lastData);

  const noDataMessage =
    site.lastData === null
      ? t('common:site-settings.session-capture.never-data')
      : t('common:site-settings.session-capture.no-data', { lastData: site.lastData });

  return (
    <Container
      css={css`
        background-color: ${hasData ? 'transparent' : tokens.color.bg_warning};
      `}
    >
      <Row>
        <div>
          <Title>
            <span>{t('common:site-settings.session-capture.session-capture')}</span>
            &nbsp;
            {isEnabled ? <Enabled isWarning={!hasData} /> : <Disabled isWarning={!hasData} />}
          </Title>

          {hasData ? (
            <Info>
              {isEnabled && !unlimitedSessions && (
                <>
                  {t('common:site-settings.session-capture.capturing-daily', {
                    allowance: ccDailyAllowance,
                  })}
                  &nbsp;
                  <Link
                    as="button"
                    kind="inline"
                    data-testid="upsell"
                    onClick={() => {
                      upsell('site.settings-session_capture_toggle-increase.daily.limit');
                    }}
                  >
                    {t('common:site-settings.session-capture.upgrade-to-increase')}
                  </Link>
                  .
                </>
              )}
              {isEnabled &&
                unlimitedSessions &&
                t('common:site-settings.session-capture.capturing-daily-unlimited')}
            </Info>
          ) : (
            <Info>
              <AlertIcon size={16} icon="alert" color={tokens.color.white} />

              <span>
                {noDataMessage}
                &nbsp;
                <StyledLink
                  onClick={() => {
                    trackClick('site.settings-no_data_tracking_issue_warning_verify-installation');
                    showModalTrackingCode(site, 2, 'Session Capture');
                  }}
                >
                  {t('common:site-settings.session-capture.verify')}
                </StyledLink>
                .
              </span>
            </Info>
          )}
        </div>

        <Tooltip
          enabled={!hasSessionSettingsUpdatePermission}
          content={t('common:site-settings.session-capture.read-only') as string}
        >
          <span>
            <Switch
              noText
              labelPlacement="left"
              disabled={!hasSessionSettingsUpdatePermission}
              checked={isEnabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  trackClick('site.settings_enable_session_capture');
                } else {
                  trackClick('site.settings_disable_session_capture');
                }
                saveCaptureMode(event.target.checked);
              }}
            />
          </span>
        </Tooltip>
      </Row>
    </Container>
  );
};

// FIXME: import/no-default-export
// eslint-disable-next-line import/no-default-export
export default SessionCapture;
